import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonInput, IonLabel, IonItem, IonList, IonModal, IonContent } from "@ionic/react";
import { Helmet } from "react-helmet";

const GreaterLessThan: React.FC = () => {
    const [number1, setNumber1] = useState<number>(0);
    const [number2, setNumber2] = useState<number>(0);
    const [userInput, setUserInput] = useState<string>("");
    const [feedback, setFeedback] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate new question
    const generateQuestion = () => {
        setNumber1(Math.floor(Math.random() * 90) + 10); // Random two-digit number
        setNumber2(Math.floor(Math.random() * 90) + 10);
        setUserInput("");
        setFeedback(null);
    };

    // Check user answer
    const checkAnswer = (operator: string) => {
        let correctOperator;
        if (number1 > number2) {
            correctOperator = ">";
        } else if (number1 < number2) {
            correctOperator = "<";
        } else {
            correctOperator = "=";
        }

        if (operator === correctOperator) {
            setShowImage(true);
            setFeedback("Correct! Generating a new question...");
            setTimeout(() => {
                generateQuestion();
                setShowImage(false);
                setError(false);
            }, 5000); // Generate a new question after 2 seconds
        } else {
            setError(true);
        }
    };

    // Generate the first question on component mount
    useEffect(() => {
        generateQuestion();
        document.title = "Greater Than Less Than Worksheet for LKG | Compare Numbers 1–20 | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach LKG students the concepts of greater than, less than, and equal to with interactive activities on Mama’s Guidance—designed to build comparison and critical thinking skills through fun and engaging math exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className='contentPart additionWord pickColor matchImage match-container'>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Greater Than, Less Than</h1>

                                    <p>
                                        Learning to compare numbers helps children build a strong understanding of value and quantity. In this LKG-level worksheet, kids will explore the concepts of <strong>greater than</strong>, <strong>less than</strong>, and <strong>equal to</strong> using numbers from 1 to 20. Through colorful visuals and fun exercises, your child will begin to make sense of number relationships.
                                    </p>

                                    <h2>🔍 What Is Greater Than, Less Than?</h2>
                                    <p>
                                        We compare two numbers and determine which one is larger, smaller, or equal:
                                    </p>
                                    <ul>
                                        <li><strong>Greater Than (&gt;):</strong> 7 &gt; 3 → 7 is greater than 3</li>
                                        <li><strong>Less Than (&lt;):</strong> 2 &lt; 6 → 2 is less than 6</li>
                                        <li><strong>Equal To (=):</strong> 4 = 4 → both are the same</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify and compare numbers between 1 and 100</li>
                                        <li>Understand the meaning of <strong>greater than, less than, and equal to</strong></li>
                                        <li>Recognize and use comparison symbols (&gt;, &lt;, =)</li>
                                        <li>Improve number sense and logical reasoning</li>
                                    </ul>

                                    <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Compare numbers using alligator mouth symbols</li>
                                        <li>Circle the greater or smaller number</li>
                                        <li>Fill in the correct symbol (&gt; &lt; =)</li>
                                        <li>Object-based comparison tasks (<strong>e.g., 5 apples vs. 8 apples</strong>)</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                    <ul>
                                        <li>Use <strong>“alligator mouth”</strong> storytelling – the alligator always eats the bigger number!</li>
                                        <li>Show quantity using real objects like pencils or blocks</li>
                                        <li>Draw smiley faces on comparison symbols to make them fun and memorable</li>
                                        <li>Practice number flashcards and ask <strong>“Which is more?”</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Turn the activity into a game: draw two cards with numbers and ask your child to <strong>“feed the bigger number to the alligator!”</strong>
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Greater Than, Less Than</h1>

                                <p>
                                    Learning to compare numbers helps children build a strong understanding of value and quantity. In this LKG-level worksheet, kids will explore the concepts of <strong>greater than</strong>, <strong>less than</strong>, and <strong>equal to</strong> using numbers from 1 to 20. Through colorful visuals and fun exercises, your child will begin to make sense of number relationships.
                                </p>

                                <h2>🔍 What Is Greater Than, Less Than?</h2>
                                <p>
                                    We compare two numbers and determine which one is larger, smaller, or equal:
                                </p>
                                <ul>
                                    <li><strong>Greater Than (&gt;):</strong> 7 &gt; 3 → 7 is greater than 3</li>
                                    <li><strong>Less Than (&lt;):</strong> 2 &lt; 6 → 2 is less than 6</li>
                                    <li><strong>Equal To (=):</strong> 4 = 4 → both are the same</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and compare numbers between 1 and 100</li>
                                    <li>Understand the meaning of <strong>greater than, less than, and equal to</strong></li>
                                    <li>Recognize and use comparison symbols (&gt;, &lt;, =)</li>
                                    <li>Improve number sense and logical reasoning</li>
                                </ul>

                                <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Compare numbers using alligator mouth symbols</li>
                                    <li>Circle the greater or smaller number</li>
                                    <li>Fill in the correct symbol (&gt; &lt; =)</li>
                                    <li>Object-based comparison tasks (<strong>e.g., 5 apples vs. 8 apples</strong>)</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                <ul>
                                    <li>Use <strong>“alligator mouth”</strong> storytelling – the alligator always eats the bigger number!</li>
                                    <li>Show quantity using real objects like pencils or blocks</li>
                                    <li>Draw smiley faces on comparison symbols to make them fun and memorable</li>
                                    <li>Practice number flashcards and ask <strong>“Which is more?”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn the activity into a game: draw two cards with numbers and ask your child to <strong>“feed the bigger number to the alligator!”</strong>
                                </p>
                            </article>
                            <div className="worksheet-container text-center" style={{ marginTop: '70px', fontSize: '40px' }}>
                                <div className="text-center" style={{ marginLeft: 0, display: 'flex', gap: '30px' }}>
                                    <div className="grid-box">    {number1} </div>
                                    <div className="grid-box" style={{ width: '50px' }}>
                                        ?
                                    </div>
                                    <div className="grid-box">    {number2}</div>
                                </div>

                                <div className=" mt-4 text-center" style={{ marginLeft: 0, display: 'flex', gap: '30px' }}>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer(">")}>&gt;</div>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer("<")}>&lt;</div>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer("=")}>=</div>
                                </div>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GreaterLessThan;
